<template>
	<form class="vcx-email-capture-form" novalidate @submit.prevent>
		<transition name="transition--fade" mode="out-in">
			<div v-if="!submitSuccess" key="form" class="vcx-flex vcx-flex-col">
				<div class="vcx-lead-capture-wrapper">
					<input
						v-model="v$.email.$model"
						type="email"
						:placeholder="placeholder"
						aria-label="VCX Email input"
						class="vcx-text-input"
						:class="{ 'vcx-text-input--error': v$.email.$error }"
						:aria-invalid="v$.email.$error"
						:readonly="isSubmitting"
						aria-describedby="vcx-email-error"
						data-test="vcx-lead-capture-input"
						@blur="v$.email.$touch()"
					/>
					<VCXSubmitButton
						:is-working="isSubmitting"
						:v$="v$"
						data-test="vcx-lead-capture-submit"
						classes="vcx-button-primary"
						@sb-click="handleSubmit()"
					>
						{{ buttonText }}
					</VCXSubmitButton>
				</div>
				<p v-if="v$.email.$error" class="vcx-text-center vcx-body-md" role="alert" data-test="vcx-email-error">
					<BaseSvgIcon name="error" width="24" height="24" class="vcx-mr-25" />
					<span v-if="v$.email.required.$invalid">Email is required.</span>
					<span v-else-if="v$.email.email.$invalid">Please enter a valid email address.</span>
				</p>
				<p v-if="submitError" class="vcx-text-center vcx-body-md" role="alert" data-test="vcx-submit-error">
					{{ submitError }}
				</p>
				<p v-if="supportText" class="vcx-body-md vcx-text-center">{{ supportText }}</p>
			</div>
			<div v-else key="success" class="vcx-text-center vcx-body-lg" role="status" data-test="vcx-submit-success">
				<p>Thanks for signing up.</p>
				<p>You'll get updated when VCX begins trading.</p>
			</div>
		</transition>
	</form>
</template>

<script setup lang="ts">
import { computed, reactive, ref, type Ref } from 'vue';
import { email, required } from '@vuelidate/validators';
import BaseSvgIcon from '@components/_base-svg-icon.vue';
import { flattenQueryParam } from '@utils/request';
import { iterableSubscriptionUrl } from '@constants/vcx';
import { logError } from '@utils/error-tracking';
import { useMixpanel } from '@utils/composables/use-mixpanel';
import { useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import VCXSubmitButton from '@components/vcx/vcx-submit-button.vue';

const route = useRoute();
const { trackMixpanelEvent } = useMixpanel();

interface Props {
	placeholder?: string;
	buttonText?: string;
	supportText?: string;
}

withDefaults(defineProps<Props>(), {
	placeholder: 'Email address',
	buttonText: 'Get updates',
	supportText: ''
});

interface FormState {
	email: string;
}

const formState: FormState = reactive({
	email: ''
});

const rules = computed(() => ({
	email: {
		required,
		email
	}
}));

const v$ = useVuelidate(rules, formState);
const isSubmitting: Ref<boolean> = ref(false);
const submitSuccess: Ref<boolean> = ref(false);
const submitError: Ref<string> = ref('');

async function handleSubmit(): Promise<void> {
	isSubmitting.value = true;
	submitSuccess.value = false;
	submitError.value = '';

	try {
		const formData = new FormData();
		formData.append('email', formState.email);

		await fetch(iterableSubscriptionUrl, {
			method: 'POST',
			body: formData,
			mode: 'no-cors'
		});

		trackMixpanelEvent('Non-shopping Lead Captured', {
			'Nonshopping Lead Type': 'VCX_EMAIL_LEAD',
			'Acquisition Method': 'EMAIL_CAPTURE',
			'Acquisition Page': route.path,
			'UTM Source': flattenQueryParam(route.query.utm_source) ?? undefined,
			'UTM Campaign': flattenQueryParam(route.query.utm_campaign) ?? undefined,
			'UTM Medium': flattenQueryParam(route.query.utm_medium) ?? undefined,
			'UTM Content': flattenQueryParam(route.query.utm_content) ?? undefined
		});

		submitSuccess.value = true;
		formState.email = '';
		v$.value.$reset();
	} catch (e) {
		submitError.value = 'An error occurred. Please try again.';
		const error = e as Error;
		logError(error, 'error', {
			key: 'VCX Lead Capture Form Submission Error',
			data: {
				email: formState.email
			}
		});
	} finally {
		isSubmitting.value = false;
	}
}
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive' as *;

.vcx-lead-capture-wrapper {
	position: relative;
	width: 100%;
	height: 64px;
}

.vcx-button-primary {
	position: absolute;
	top: 50%;
	right: var(--vcx-space-50);
	transform: translateY(-50%);

	@include vcx-media-smo {
		font-size: var(--vcx-font-size-body-sm);
	}
}
</style>
